'use client';
import { useCallback, useEffect, useState } from 'react';
import { safeClientCallback } from '@/modules/server/helpers-client';
import { useTrackingUserParamsStore } from '@/modules/userTrackingParams';
import { ANROID_AND_IOS_APP_SOURCE } from '@/modules/userTrackingParams/constants';
import { checkStandalone, trackStandaloneChange } from '../helpers';

export enum Platform {
  DESKTOP = 'desktop',
  IOS = 'ios',
  ANDROID = 'android',
}

/**
 * This hook is used to determine the app environment based on the user agent and display mode.
 * It sets the environment to either 'desktop', 'mobile', 'desktop'.
 * @returns {string} The app environment.
 */
const checkUserAgentPlatform = safeClientCallback((): Platform => {
  const userAgent = navigator.userAgent.toLowerCase();

  const isAndroid = /android/.test(userAgent);
  const isIOS = /iphone|ipad|ipod/.test(userAgent);

  let platform = Platform.DESKTOP;
  if (isIOS) platform = Platform.IOS;
  if (isAndroid) platform = Platform.ANDROID;

  return platform;
}, Platform.DESKTOP);

export function useAppEnvironment(): string {
  const defaultPlatform = checkUserAgentPlatform();
  const defaultAppType = checkStandalone() ? 'pwa' : 'web';
  const [env, setEnv] = useState<string>(
    `${defaultAppType}-${defaultPlatform}`
  );

  const source = useTrackingUserParamsStore((state) => state.source);

  const checkPlatformType = useCallback((isStandalone: boolean) => {
    const platform = checkUserAgentPlatform();
    if (source === ANROID_AND_IOS_APP_SOURCE) {
      setEnv(`app-${platform}`);
    } else {
      setEnv(`${isStandalone ? 'pwa' : 'web'}-${platform}`);
    }
  }, []);

  useEffect(() => {
    const unsubscribe = trackStandaloneChange(checkPlatformType);
    return unsubscribe;
  }, [checkPlatformType]);

  return env;
}
